import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { SeatLine, Segment, TicketLine, Traveler } from '../../lib/types';

/** Filled glyphs, drawn the way the host's icon font draws them. */
const ICONS = {
  pnr: 'M3 4h18v16H3zM6 8h2v2H6zm4 0h8v2h-8zm-4 4h2v2H6zm4 0h8v2h-8z',
  travelers:
    'M8 11a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 8 11Zm8 0a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4ZM1.5 19c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2zm13.6 0c0-2.1-.8-4-2.1-5.3a6 6 0 0 1 3-.8c3.1 0 5.5 2.3 5.5 6.1z',
  air: 'M21 15.5v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0v5l-8 5v2l8-2.5v5.5l-2 1.5v1.5l3.5-1 3.5 1V20l-2-1.5V13z',
  hotel: 'M2 18V6h2v8h7V9h8a3 3 0 0 1 3 3v6h-2v-2H4v2zm5-6a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  car: 'M5 17v2H3v-7l2-5.5A1.5 1.5 0 0 1 6.4 5.5h11.2a1.5 1.5 0 0 1 1.4 1l2 5.5v7h-2v-2zM6.3 11h11.4l-1.3-3.8H7.6zM7 15a1.3 1.3 0 1 0 0-2.6A1.3 1.3 0 0 0 7 15zm10 0a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6z',
  tickets:
    'M2 7.5A1.5 1.5 0 0 1 3.5 6h17A1.5 1.5 0 0 1 22 7.5V10a2 2 0 0 0 0 4v2.5a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 16.5V14a2 2 0 0 0 0-4zM6 9v6h12V9z',
  seats: 'M6 3h2.5v10H16l3 7.5h-2.6L14 15H6.5A2.5 2.5 0 0 1 4 12.5V5a2 2 0 0 1 2-2zm-3 12h2v5H3z',
  airExtras:
    'M9 3.5h6a1 1 0 0 1 1 1V7h-1.6V5.1H9.6V7H8V4.5a1 1 0 0 1 1-1zM5.6 7h12.8A1.6 1.6 0 0 1 20 8.6v10.3a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 18.9V8.6A1.6 1.6 0 0 1 5.6 7z',
} as const;

function Glyph({ path, size = 16 }: { path: string; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d={path} fill="currentColor" fillRule="evenodd" />
    </svg>
  );
}

/** The amber flag on a segment that is booked but not yet priced or ticketed. */
function Warning() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="trip__warning">
      <path d="M12 2.5 23 21.5H1z" fill="currentColor" />
      <path d="M11 9h2v6.5h-2zm0 8h2v2h-2z" fill="var(--sedl-white)" />
    </svg>
  );
}

function Kebab() {
  return (
    <span className="trip__kebab" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="16" height="16">
        <circle cx="12" cy="5" r="2" fill="currentColor" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
        <circle cx="12" cy="19" r="2" fill="currentColor" />
      </svg>
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" className="trip__chevron">
      <path d={open ? 'M3 10.5l5-5 5 5' : 'M3 5.5l5 5 5-5'} fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** 20JAN as the summary writes it: 20 Jan. */
function shortDate(ddmmm: string): string {
  if (ddmmm.length !== 5) return ddmmm;
  const month = ddmmm.slice(2);
  return `${ddmmm.slice(0, 2)} ${month[0]}${month.slice(1).toLowerCase()}`;
}

interface SectionProps {
  label: string;
  icon: ReactNode;
  count: number;
  open: boolean;
  onToggle: () => void;
  children?: ReactNode;
}

function Section({ label, icon, count, open, onToggle, children }: SectionProps) {
  const empty = count === 0;
  return (
    <div className={`trip__section${empty ? ' trip__section--empty' : ''}`}>
      <button type="button" className="trip__section-head" aria-expanded={open} onClick={onToggle}>
        {icon && <span className="trip__icon">{icon}</span>}
        <span className="trip__section-label">
          {label} ({count})
        </span>
        <Chevron open={open} />
      </button>
      {open &&
        (empty ? <p className="trip__empty">No {label.toLowerCase()} in this trip.</p> : <ul className="trip__items">{children}</ul>)}
    </div>
  );
}

interface TripSummaryProps {
  travelers: Traveler[];
  itinerary: Segment[];
  /** Tickets issued on the PNR in the work area. */
  tickets: TicketLine[];
  /** Seats assigned in the work area. */
  seats: SeatLine[];
  /** Shown in place of "PNR" once the PNR is filed. */
  recordLocator: string;
  /** Sends a host entry, as the End & Retrieve menu does. */
  onCommand: (entry: string) => void;
  busy: boolean;
  onToggleCollapsed: () => void;
}

const EMPTY_SECTIONS: { key: keyof typeof ICONS; label: string }[] = [
  { key: 'hotel', label: 'Hotel' },
  { key: 'car', label: 'Car' },
  { key: 'airExtras', label: 'Air Extras' },
];

/**
 * The itinerary rail. A section with something in it opens on its own, as the
 * host does once a name or a segment lands in the PNR; the agent can still
 * fold it away.
 */
export function TripSummary({ travelers, itinerary, tickets, seats, recordLocator, onCommand, busy, onToggleCollapsed }: TripSummaryProps) {
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, count: number) => toggled[key] ?? count > 0;
  const toggle = (key: string, count: number) =>
    setToggled((prev) => ({ ...prev, [key]: !(prev[key] ?? count > 0) }));

  return (
    <aside className="trip" aria-label="Trip summary">
      <header className="trip__header">
        <button type="button" className="trip__collapse" onClick={onToggleCollapsed} aria-label="Hide trip summary">
          «
        </button>
        <h2 className="trip__title">Trip Summary</h2>
      </header>

      <div className="trip__row">
        <span className="trip__icon">
          <Glyph path={ICONS.pnr} />
        </span>
        {recordLocator || 'PNR'}
      </div>

      <button type="button" className="trip__row trip__add" disabled>
        <span className="trip__icon">
          <span className="trip__add-plus" aria-hidden="true">
            +
          </span>
        </span>
        Add to PNR
      </button>

      <div className="trip__sections">
        <Section
          label="Travelers"
          icon={<Glyph path={ICONS.travelers} />}
          count={travelers.length}
          open={isOpen('travelers', travelers.length)}
          onToggle={() => toggle('travelers', travelers.length)}
        >
          {travelers.map((t) => (
            <li key={t.number} className="trip__item">
              <span className="trip__item-text">
                <b>{t.number}</b> {t.last}, {t.first} ({t.ptc})
              </span>
              <Kebab />
            </li>
          ))}
        </Section>

        <Section
          label="Air"
          icon={itinerary.length > 0 ? <Warning /> : <Glyph path={ICONS.air} />}
          count={itinerary.length}
          open={isOpen('air', itinerary.length)}
          onToggle={() => toggle('air', itinerary.length)}
        >
          {itinerary.map((s) => (
            <li key={s.line} className="trip__item">
              <Warning />
              <span className="trip__item-text">
                <b>
                  {s.origin}
                  <span className="trip__arrow">▸</span>
                  {s.destination}
                </b>{' '}
                {shortDate(s.date)} {s.depart}-{s.arrive}
                {s.dayChange !== 0 && (s.dayChange > 0 ? `+${s.dayChange}` : s.dayChange)}
              </span>
              <span className="trip__item-status">
                {s.class} {s.status}
                {s.seats}
              </span>
              <Kebab />
            </li>
          ))}
        </Section>

        {EMPTY_SECTIONS.slice(0, 2).map(({ key, label }) => (
          <Section
            key={key}
            label={label}
            icon={<Glyph path={ICONS[key]} />}
            count={0}
            open={isOpen(key, 0)}
            onToggle={() => toggle(key, 0)}
          />
        ))}

        <Section
          label="Tickets"
          icon={<Glyph path={ICONS.tickets} />}
          count={tickets.length}
          open={isOpen('tickets', tickets.length)}
          onToggle={() => toggle('tickets', tickets.length)}
        >
          {tickets.map((t) => (
            <li key={t.number} className="trip__item">
              <span className="trip__item-text trip__ticket">
                TE {t.number}-{t.country} {t.name} {t.pcc}*{t.agent}{' '}
                <span className="trip__ticket-issued">
                  {shortDate(t.issued)} *{t.status}
                </span>
              </span>
              <Kebab />
            </li>
          ))}
        </Section>

        <Section
          label="Seats"
          icon={<Glyph path={ICONS.seats} />}
          count={seats.length}
          open={isOpen('seats', seats.length)}
          onToggle={() => toggle('seats', seats.length)}
        >
          {seats.map((s) => (
            <li key={`${s.segment}-${s.traveler}`} className="trip__item">
              <span className="trip__item-text">
                <b>
                  {s.origin}
                  <span className="trip__arrow">▸</span>
                  {s.destination}
                </b>{' '}
                {s.carrier}
                {s.number} {s.passenger}
              </span>
              <span className="trip__item-status">
                <b>{s.seat}</b>
                {s.characteristic && ` ${s.characteristic}`} {s.status}
              </span>
              <Kebab />
            </li>
          ))}
        </Section>

        {EMPTY_SECTIONS.slice(2).map(({ key, label }) => (
          <Section
            key={key}
            label={label}
            icon={<Glyph path={ICONS[key]} />}
            count={0}
            open={isOpen(key, 0)}
            onToggle={() => toggle(key, 0)}
          />
        ))}

        <Section label="Other" icon={null} count={0} open={isOpen('other', 0)} onToggle={() => toggle('other', 0)} />
      </div>

      <EndMenu onCommand={onCommand} busy={busy} />
    </aside>
  );
}

/** What the menu beside End & Retrieve offers, with the entry each one sends. */
const END_OPTIONS: { label: string; hint: string; keys: string; entry: string; confirm?: boolean }[] = [
  { label: 'End PNR', hint: 'End transaction', keys: 'E/ET', entry: 'E' },
  { label: 'End & Retrieve PNR', hint: 'End transaction & redisplay', keys: 'ER', entry: 'ER' },
  { label: 'Ignore PNR', hint: 'Ignore transaction', keys: 'I', entry: 'I', confirm: true },
  { label: 'Ignore & Retrieve PNR', hint: 'Ignore transaction & redisplay', keys: 'IR', entry: 'IR' },
  { label: 'Ignore/End & Clone PNR', hint: 'Ignore/End transaction & clone', keys: 'IC/EC', entry: 'EC' },
];

function EndMenu({ onCommand, busy }: { onCommand: (entry: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The menu closes on a click anywhere else, or on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!confirming) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setConfirming(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirming]);

  const choose = (option: (typeof END_OPTIONS)[number]) => {
    setOpen(false);
    if (option.confirm) setConfirming(true);
    else onCommand(option.entry);
  };

  return (
    <div className="trip__footer" ref={ref}>
      {open && (
        <ul className="endmenu" role="menu">
          {END_OPTIONS.map((option) => (
            <li key={option.entry} role="none">
              <button type="button" role="menuitem" className="endmenu__item" onClick={() => choose(option)}>
                <span className="endmenu__label">{option.label}</span>
                <span className="endmenu__keys">{option.keys}</span>
                <span className="endmenu__hint">{option.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="trip__end-group">
        <button type="button" className="trip__end" disabled={busy} onClick={() => onCommand('ER')}>
          End &amp; Retrieve
        </button>
        <button
          type="button"
          className="trip__end trip__end-more"
          aria-label="More end and ignore options"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={busy}
          onClick={() => setOpen((o) => !o)}
        >
          <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
            <path d="M1 7h8L5 3z" fill="currentColor" />
          </svg>
        </button>
      </div>

      {confirming && (
        <div className="confirm__backdrop" onMouseDown={() => setConfirming(false)}>
          <div
            className="confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-ignore-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="confirm__head">
              <h2 id="confirm-ignore-title">Ignore</h2>
              <button type="button" className="confirm__close" aria-label="Close" onClick={() => setConfirming(false)}>
                ✕
              </button>
            </div>
            <p className="confirm__body">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M12 2.5 23 21.5H1z" fill="#f0a020" />
                <path d="M11 9h2v6.5h-2zm0 8h2v2h-2z" fill="#fff" />
              </svg>
              Changes will not be saved.
            </p>
            <div className="confirm__actions">
              <button type="button" className="sold__button" autoFocus onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="sold__button sold__button--primary"
                onClick={() => {
                  setConfirming(false);
                  onCommand('I');
                }}
              >
                Ignore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
